import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';

@Component({
  selector: 'ciwc-composite-cable-calculator',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
  ],
  templateUrl: './composite-cable-calculator.component.html',
  styleUrls: ['./composite-cable-calculator.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompositeCableCalculatorComponent {
  private readonly destroyRef = inject(DestroyRef);

  calculatorForm: FormGroup;
  totalDiameter = 0;

  get cables() {
    return this.calculatorForm.get('cables') as FormArray;
  }

  constructor(
    private dialogRef: MatDialogRef<CompositeCableCalculatorComponent>,
    private fb: FormBuilder,
  ) {
    this.calculatorForm = this.fb.group({
      cables: this.fb.array(this.createCableRows()),
    });

    // Subscribe to cables array changes
    this.cables.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.calculate();
    });
  }

  onClose(): void {
    this.dialogRef.close();
  }

  private createCableRows(): FormGroup[] {
    const rows: FormGroup[] = [];
    // Create 15 rows as shown in the screenshot
    for (let i = 0; i < 15; i++) {
      // Pre-populate first two rows as shown in the screenshot
      const wireDiameter = null;
      const wireCount = null;

      rows.push(
        this.fb.group({
          wireDiameter: [wireDiameter, [Validators.min(0)]],
          wireCount: [wireCount, [Validators.min(0)]],
        }),
      );
    }
    return rows;
  }

  private calculate(): void {
    let sumInsideTheTable = 0;

    // Calculate the sum of (diameter^2 * count) for each cable type
    for (const cable of this.cables.controls) {
      const diameter = cable.get('wireDiameter')?.value;
      const count = cable.get('wireCount')?.value;

      if (diameter !== null && diameter !== undefined && count !== null && count !== undefined) {
        const currentIterationValue = Math.pow(diameter, 2) * count;
        sumInsideTheTable += currentIterationValue;
      }
    }

    // Calculate the final diameter using the formula: 1.2 * sqrt(sum)
    if (sumInsideTheTable > 0) {
      const fullFormula = 1.2 * Math.sqrt(sumInsideTheTable);
      // Round to 2 decimal places
      this.totalDiameter = Math.round((fullFormula + Number.EPSILON) * 1000) / 1000;
    } else {
      this.totalDiameter = 0;
    }
  }
}
